import classNames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Carousel, Button } from 'antd';
import { CarouselRef } from 'antd/lib/carousel';
import { ReactComponent as CrossSvg } from '@assets/icons/cross.svg';
import { ReactComponent as ArrowLeftSvg } from '@assets/icons/arrow-left.svg';
import { ReactComponent as ArrowRightSvg } from '@assets/icons/arrow-right.svg';
import AnnouncementOne from '@assets/images/approval/announcement-1.jpg';
import AnnouncementTwo from '@assets/images/approval/announcement-2.jpg';
import AnnouncementThree from '@assets/images/approval/announcement-3.jpg';
import AnnouncementFour from '@assets/images/approval/announcement-4.jpg';
import AnnouncementFive from '@assets/images/approval/announcement-5.jpg';

import './ApprovalFeatureAnnouncementModal.less';

export type ApprovalFeatureAnnouncementModalProps = {
  onClose: () => void;
  open: boolean;
};

export default function ApprovalFeatureAnnouncementModal({
  open,
  onClose
}: ApprovalFeatureAnnouncementModalProps) {
  const carouselRef = useRef<CarouselRef>(null);
  const [activeSlide, setActiveSlide] = useState(0);

  const items = useMemo(() => {
    return [
      {
        title: (
          <>
            Approvals are now in
            <br />
            your Main Navigation
          </>
        ),
        image: AnnouncementOne,
        text: (
          <>
            Previously, approvals were only available within each project. We’ve
            added a dedicated <strong>Approvals</strong> tab in the main
            navigation so you can easily find and manage them in one place.
          </>
        )
      },
      {
        title: (
          <>
            Streamlined
            <br />
            Bulk Approvals
          </>
        ),
        image: AnnouncementTwo,
        text: (
          <>
            Manage multiple approvals at once with our new streamlined flow.
            Select your media, create a single approval route, and keep projects
            moving faster and more organized.
          </>
        )
      },
      {
        title: (
          <>
            New Approval Widget
            <br /> on Dashboard
          </>
        ),
        image: AnnouncementThree,
        text: (
          <>
            An updated approval widget is now available on the{' '}
            <strong>Dashboard</strong>. It shows all your current approvals,
            deadlines, and progress in one place, so you can track tasks without
            switching between screens.
          </>
        )
      },
      {
        title: (
          <>
            See Approvals
            <br />
            Assigned to You
          </>
        ),
        image: AnnouncementFour,
        text: (
          <>
            In the new <strong>Approvals</strong> tab, you’ll find a{' '}
            <strong>Review Approvals</strong> section. This is where you can
            quickly access and review all approvals that require your attention.
          </>
        )
      },
      {
        title: (
          <>
            Split Media into
            <br /> New Approval
          </>
        ),
        image: AnnouncementFive,
        text: (
          <>
            We’ve added a new option to help you manage approvals more flexibly.
            Use <strong>Split into new approval</strong> to separate media into
            a new approval flow and keep your reviews clear and organized.
          </>
        )
      }
    ];
  }, []);

  const dots = useMemo(() => new Array(items.length).fill(''), [items]);

  const onCancel = useCallback(() => {
    onClose();
    setActiveSlide(0);
  }, [onClose, setActiveSlide]);

  const goNext = useCallback(() => {
    carouselRef.current?.next();
  }, []);

  const goPrev = useCallback(() => {
    carouselRef.current?.prev();
  }, []);

  const goTo = useCallback((index: number) => {
    carouselRef.current?.goTo(index);
  }, []);

  return (
    <Modal
      open={open}
      footer={null}
      centered={true}
      closeIcon={<CrossSvg />}
      destroyOnClose
      width={434}
      maskClosable={false}
      onCancel={onCancel}
      className="approval-feature-announcement-modal"
    >
      <div className="modal_container">
        <Carousel
          dots={false}
          ref={carouselRef}
          afterChange={(index) => setActiveSlide(index)}
        >
          {items.map((item, index) => (
            <div
              key={index}
              className="approval-feature-announcement-modal_block"
            >
              <div className="approval-feature-announcement-modal_image">
                <img src={item.image} alt="Announcement" />
              </div>
              <div className="approval-feature-announcement-modal_title">
                {item.title}
              </div>
              <div className="approval-feature-announcement-modal_text">
                {item.text}
              </div>
            </div>
          ))}
        </Carousel>
        <div className="approval-feature-announcement-modal_bottom">
          <div className="approval-feature-announcement-modal_controls">
            <Button
              type="text"
              icon={<ArrowLeftSvg />}
              onClick={goPrev}
              disabled={activeSlide === 0}
              size="small"
            />
            <div className="approval-feature-announcement-modal_dots">
              {dots.map((_item, index) => (
                <div
                  key={index}
                  className={classNames(
                    'approval-feature-announcement-modal_dots-item',
                    {
                      active: index === activeSlide
                    }
                  )}
                  onClick={() => goTo(index)}
                />
              ))}
            </div>
            <Button
              type="text"
              icon={<ArrowRightSvg />}
              onClick={goNext}
              disabled={activeSlide === items.length - 1}
              size="small"
            />
          </div>
          <Button type="primary" onClick={onCancel} style={{ minWidth: 130 }}>
            Get started
          </Button>
        </div>
      </div>
    </Modal>
  );
}
